import type { SeenMedplumClient } from '@seen/core/client'
import { getFilenameFromContentDisposition } from '@seen/core/utils'
import type { SeenPdfApi } from '@seen/react'
import {
  type PdfExportRequest,
  pdfExportPath,
} from '@seen/request-types/pdfExports'
import { omit } from 'lodash-es'
import { createAuthFetch } from '#src/api/ai.ts'

// Everything but the kind and the record id travels in the body.
function exportBody(request: PdfExportRequest) {
  return omit(request, [
    'kind',
    'participantId',
    'orderId',
    'carePlanId',
    'appointmentId',
  ])
}

// The routes answer with a PDF body, not JSON, so they are called with the
// authenticated fetch rather than the typed RPC client. They serve the
// Lambda API's paths under /api.
export function createPortalPdfApi(medplum: SeenMedplumClient): SeenPdfApi {
  const authFetch = createAuthFetch(medplum)
  return {
    async exportPdf(request) {
      const response = await authFetch(`/api${pdfExportPath(request)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(exportBody(request)),
      })
      if (!response.ok) {
        const body: { error?: string } = await response.json().catch(() => ({}))
        throw new Error(body.error ?? `PDF export failed (${response.status})`)
      }
      return {
        blob: await response.blob(),
        filename: getFilenameFromContentDisposition(
          response.headers.get('content-disposition') ?? undefined
        ),
      }
    },
  }
}
